import React, { useState, useMemo, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useSelector, useDispatch } from "react-redux";
import { getProfile } from "../../utils";
import {
  Avatar,
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  IconButton,
} from "@mui/material";
import { EditIcon } from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { updateUserData } from "../../utils/store/slice/userSlice";
import { toast } from "react-toastify";

const EmployeeProfile = () => {
  const { userData: user, token } = useSelector((state) => state.userSlice);
  const dispatch = useDispatch();

  const [active, setActive] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [filters, setFilters] = useState({
    dateRange: "all",
    sortBy: "newest",
  });

  // Name Edit Modal States
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    if (editModalOpen) {
      setFirstName(user?.first_name || "");
      setLastName(user?.last_name || "");
    }
  }, [editModalOpen, user?.first_name, user?.last_name]);

  // Analytics calculations
  const analytics = useMemo(() => {
    const history = user?.history || [];

    if (history.length === 0) {
      return {
        totalRedemptions: 0,
        totalCoins: 0,
        avgCoins: 0,
        activityData: [],
        monthlyData: [],
        thisMonthCoins: 0,
        thisMonthRedemptions: 0,
        topActivities: [],
      };
    }

    const totalRedemptions = history.length;
    const totalCoins = history.reduce(
      (sum, h) => sum + parseInt(h.coins || 0),
      0
    );
    const avgCoins = Math.round(totalCoins / totalRedemptions) || 0;

    // Activity breakdown
    const activityMap = {};
    history.forEach((h) => {
      activityMap[h.activity] =
        (activityMap[h.activity] || 0) + parseInt(h.coins || 0);
    });
    const activityData = Object.entries(activityMap).map(([name, value]) => ({
      name,
      value,
      count: history.filter((h) => h.activity === name).length,
    }));

    // Monthly trend
    const monthlyMap = {};
    history.forEach((h) => {
      const month = new Date(h.timestamp).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      if (!monthlyMap[month]) {
        monthlyMap[month] = { month, coins: 0, redemptions: 0 };
      }
      monthlyMap[month].coins += parseInt(h.coins || 0);
      monthlyMap[month].redemptions += 1;
    });
    const monthlyData = Object.values(monthlyMap).sort(
      (a, b) => new Date(a.month) - new Date(b.month)
    );

    // This month stats
    const now = new Date();
    const thisMonthData = history.filter(
      (h) =>
        new Date(h.timestamp).getMonth() === now.getMonth() &&
        new Date(h.timestamp).getFullYear() === now.getFullYear()
    );
    const thisMonthCoins = thisMonthData.reduce(
      (sum, h) => sum + parseInt(h.coins || 0),
      0
    );

    const topActivities = [...activityData]
      .sort((a, b) => b.value - a.value)
      .slice(0, 3);

    return {
      totalRedemptions,
      totalCoins,
      avgCoins,
      activityData,
      monthlyData,
      thisMonthCoins,
      thisMonthRedemptions: thisMonthData.length,
      topActivities,
    };
  }, [user?.history]);

  const COLORS = [
    "#667eea",
    "#764ba2",
    "#48bb78",
    "#ed8936",
    "#4299e1",
    "#9f7aea",
  ];

  const getFilteredHistory = () => {
    let filtered = [...(user?.history || [])];
    const now = new Date();

    switch (filters.dateRange) {
      case "today":
        filtered = filtered.filter(
          (h) => new Date(h.timestamp).toDateString() === now.toDateString()
        );
        break;
      case "week":
        const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        filtered = filtered.filter((h) => new Date(h.timestamp) >= weekAgo);
        break;
      case "month":
        filtered = filtered.filter(
          (h) =>
            new Date(h.timestamp).getMonth() === now.getMonth() &&
            new Date(h.timestamp).getFullYear() === now.getFullYear()
        );
        break;
    }

    switch (filters.sortBy) {
      case "oldest":
        filtered.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
        break;
      case "highest":
        filtered.sort(
          (a, b) => parseInt(b.coins || 0) - parseInt(a.coins || 0)
        );
        break;
      case "lowest":
        filtered.sort(
          (a, b) => parseInt(a.coins || 0) - parseInt(b.coins || 0)
        );
        break;
      default:
        filtered.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }

    return filtered;
  };

  const filteredData = getFilteredHistory();
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const currentData = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const handleNameUpdate = async () => {
    try {
      setIsUpdating(true);
      const trimmedFirstName = firstName.trim();
      const trimmedLastName = lastName.trim();

      if (!trimmedFirstName && !trimmedLastName) {
        toast.warning("Please enter at least one field");
        setIsUpdating(false);
        return;
      }

      const updateData = {
        phone_no: user?.phone_no,
      };

      if (trimmedFirstName) {
        updateData.first_name = trimmedFirstName;
      }

      if (trimmedLastName) {
        updateData.last_name = trimmedLastName;
      }

      const nameUpdateRes = await axios.put(
        `${apiUrl}/user/updateusername`,
        updateData,
        {
          headers: {
            token: token,
          },
        }
      );

      if (nameUpdateRes.data.success === true) {
        const updatedUser = {
          ...user,
          first_name: trimmedFirstName || user?.first_name,
          last_name: trimmedLastName || user?.last_name,
        };

        dispatch(updateUserData(updatedUser));

        toast.success("Name updated successfully!");

        setEditModalOpen(false);
        setFirstName("");
        setLastName("");
      } else {
        toast.error("Failed to update name. Please try again");
      }
    } catch (error) {
      console.error("Error updating name:", error);
      toast.error(
        error.response?.data?.message ||
          "Failed to update name. Please try again."
      );
    } finally {
      setIsUpdating(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200 flex items-center justify-center">
        <div className="text-xl font-semibold text-slate-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-200">
      {/* Name Edit Modal */}
      <Modal
        open={editModalOpen}
        onClose={() => !isUpdating && setEditModalOpen(false)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: { xs: "90%", sm: "400px" },
            background: "linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: "20px",
            padding: "40px",
            boxShadow: "0 25px 50px rgba(0, 0, 0, 0.25)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
              borderRadius: "inherit",
              zIndex: -1,
            },
          }}
        >
          <Typography
            sx={{
              fontSize: "24px",
              fontWeight: "700",
              color: "#2d3748",
              mb: "8px",
              textAlign: "center",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Edit Your Name
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#718096",
              mb: "30px",
              textAlign: "center",
            }}
          >
            Update your profile information
          </Typography>

          <Stack spacing={3}>
            <TextField
              label="First Name"
              variant="outlined"
              fullWidth
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              disabled={isUpdating}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  "& fieldset": {
                    borderColor: "rgba(102, 126, 234, 0.3)",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(102, 126, 234, 0.6)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#4a5568",
                  fontWeight: "600",
                },
              }}
            />

            <TextField
              label="Last Name"
              variant="outlined"
              fullWidth
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              disabled={isUpdating}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  background: "rgba(255, 255, 255, 0.8)",
                  backdropFilter: "blur(10px)",
                  "& fieldset": {
                    borderColor: "rgba(102, 126, 234, 0.3)",
                    borderWidth: "2px",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(102, 126, 234, 0.6)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                  },
                },
                "& .MuiInputLabel-root": {
                  color: "#4a5568",
                  fontWeight: "600",
                },
              }}
            />

            <Stack direction="row" spacing={2} sx={{ mt: "20px" }}>
              <Button
                variant="outlined"
                fullWidth
                onClick={() => setEditModalOpen(false)}
                disabled={isUpdating}
                sx={{
                  borderRadius: "12px",
                  borderColor: "#e2e8f0",
                  color: "#4a5568",
                  fontWeight: "600",
                  py: "12px",
                  borderWidth: "2px",
                  "&:hover": {
                    borderColor: "#cbd5e0",
                    background: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                fullWidth
                onClick={handleNameUpdate}
                disabled={isUpdating}
                sx={{
                  borderRadius: "12px",
                  background:
                    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  fontWeight: "700",
                  py: "12px",
                  boxShadow: "0 8px 25px rgba(102, 126, 234, 0.3)",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                    transform: "translateY(-2px)",
                    boxShadow: "0 12px 35px rgba(102, 126, 234, 0.4)",
                  },
                  "&:disabled": {
                    background:
                      "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                  },
                }}
              >
                {isUpdating ? (
                  <CircularProgress size={20} sx={{ color: "white" }} />
                ) : (
                  "Update Name"
                )}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Modal>

      <div className="flex flex-col md:flex-row min-h-screen">
        {/* Left Sidebar */}
        <div className="w-full md:w-1/5 bg-white/90 backdrop-blur-lg rounded-r-3xl p-8 shadow-2xl">
          <div className="flex flex-col items-center">
            {/* Avatar */}
            <div className="relative mb-8">
              <Avatar
                src={getProfile(user?.profile_picture)}
                sx={{
                  width: "110px",
                  height: "110px",
                  borderRadius: "50%",
                  border: "4px solid white",
                  boxShadow: "0 15px 35px rgba(0, 0, 0, 0.15)",
                  transition: "transform 0.3s ease",
                  "&:hover": {
                    transform: "scale(1.05)",
                  },
                }}
              />
              <div className="absolute -bottom-2 -right-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                {user.user_type?.toUpperCase() || "EMPLOYEE"}
              </div>
            </div>

            {/* Employee Info Cards */}
            <div className="w-full space-y-5">
              <div className="bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl p-5 shadow-lg text-white">
                <p className="text-xs opacity-90 mb-2">Full Name</p>
                <div className="flex items-center justify-between">
                  <p className="text-lg font-bold">
                    {user.first_name} {user.last_name}
                  </p>
                  <IconButton
                    size="small"
                    onClick={() => setEditModalOpen(true)}
                    sx={{
                      color: "white",
                      padding: "6px",
                      minWidth: "auto",
                      minHeight: "auto",
                      width: "28px",
                      height: "28px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      "&:hover": {
                        opacity: 0.7,
                        background: "rgba(255, 255, 255, 0.1)",
                        transform: "scale(1.1)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <EditIcon style={{ fontSize: "16px" }} />
                  </IconButton>
                </div>
              </div>

              <div className="bg-gradient-to-br from-violet-500 to-purple-600 rounded-2xl p-5 shadow-lg text-white">
                <p className="text-xs opacity-90 mb-2">Employee ID</p>
                <p className="text-lg font-bold">{user.emp_id || "N/A"}</p>
              </div>

              <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-5 shadow-lg text-white">
                <p className="text-xs opacity-90 mb-2">Phone Number</p>
                <p className="text-sm font-semibold">{user.phone_no}</p>
              </div>

              <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 shadow-lg text-white">
                <p className="text-xs opacity-90 mb-2">Email Address</p>
                <p className="text-xs font-semibold break-words">
                  {user.email || "N/A"}
                </p>
              </div>

              <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-5 shadow-lg text-white">
                <p className="text-xs opacity-90 mb-2">Date of Birth</p>
                <p className="text-lg font-bold">
                  {user.dob
                    ? new Date(user.dob).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })
                    : "N/A"}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Content */}
        <div className="flex-1 p-8">
          {/* Navigation Tabs */}
          <div className="flex gap-4 mb-8 bg-white/80 backdrop-blur-lg rounded-2xl p-2 shadow-xl">
            {["Redemption History", "Analytics Dashboard"].map((tab, idx) => (
              <button
                key={idx}
                onClick={() => setActive(idx)}
                className={`flex-1 px-6 py-3 rounded-xl font-bold text-sm transition-all ${
                  active === idx
                    ? "bg-gradient-to-r from-purple-600 to-indigo-600 text-white shadow-lg"
                    : "text-slate-600 hover:bg-slate-100"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-6 shadow-xl text-white">
              <p className="text-xs opacity-90 mb-2">Total Redemptions</p>
              <p className="text-3xl font-bold">{analytics.totalRedemptions}</p>
              <p className="text-xs opacity-80 mt-1">All Time</p>
            </div>

            <div className="bg-gradient-to-br from-purple-600 to-indigo-600 rounded-2xl p-6 shadow-xl text-white">
              <p className="text-xs opacity-90 mb-2">Total Coins Redeemed</p>
              <p className="text-3xl font-bold">{analytics.totalCoins}</p>
              <p className="text-xs opacity-80 mt-1">Lifetime</p>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-6 shadow-xl text-white">
              <p className="text-xs opacity-90 mb-2">Average Per Redemption</p>
              <p className="text-3xl font-bold">{analytics.avgCoins}</p>
              <p className="text-xs opacity-80 mt-1">Coins</p>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl p-6 shadow-xl text-white">
              <p className="text-xs opacity-90 mb-2">This Month</p>
              <p className="text-3xl font-bold">
                {analytics.thisMonthRedemptions}
              </p>
              <p className="text-xs opacity-80 mt-1">
                {analytics.thisMonthCoins} Coins
              </p>
            </div>
          </div>

          {/* Analytics Dashboard */}
          {active === 1 && (
            <div className="space-y-6">
              {analytics.activityData.length > 0 ? (
                <>
                  {/* Charts Row 1 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monthly Trend */}
                    <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl">
                      <h3 className="text-lg font-bold text-slate-800 mb-4">
                        Monthly Redemption Trend
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={analytics.monthlyData}>
                          <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="#e2e8f0"
                          />
                          <XAxis
                            dataKey="month"
                            stroke="#64748b"
                            fontSize={12}
                          />
                          <YAxis stroke="#64748b" fontSize={12} />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              borderRadius: "12px",
                              border: "none",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="coins"
                            stroke="#667eea"
                            strokeWidth={3}
                            name="Coins"
                          />
                          <Line
                            type="monotone"
                            dataKey="redemptions"
                            stroke="#48bb78"
                            strokeWidth={3}
                            name="Count"
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Activity Breakdown Pie */}
                    <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl">
                      <h3 className="text-lg font-bold text-slate-800 mb-4">
                        Activity Distribution
                      </h3>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={analytics.activityData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, percent }) =>
                              `${name} ${(percent * 100).toFixed(0)}%`
                            }
                            outerRadius={100}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {analytics.activityData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{
                              backgroundColor: "rgba(255, 255, 255, 0.95)",
                              borderRadius: "12px",
                              border: "none",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* Activity Bar Chart */}
                  <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl">
                    <h3 className="text-lg font-bold text-slate-800 mb-4">
                      Coins by Activity
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={analytics.activityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" stroke="#64748b" fontSize={12} />
                        <YAxis stroke="#64748b" fontSize={12} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: "rgba(255, 255, 255, 0.95)",
                            borderRadius: "12px",
                            border: "none",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Legend />
                        <Bar
                          dataKey="value"
                          fill="#667eea"
                          name="Total Coins"
                          radius={[8, 8, 0, 0]}
                        />
                        <Bar
                          dataKey="count"
                          fill="#48bb78"
                          name="Redemption Count"
                          radius={[8, 8, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Top Activities */}
                  {analytics.topActivities.length > 0 && (
                    <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-6 shadow-xl">
                      <h3 className="text-lg font-bold text-slate-800 mb-4">
                        Top 3 Activities
                      </h3>
                      <div className="space-y-4">
                        {analytics.topActivities.map((activity, idx) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-xl"
                          >
                            <div className="flex items-center gap-4">
                              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold">
                                {idx + 1}
                              </div>
                              <div>
                                <p className="font-bold text-slate-800">
                                  {activity.name}
                                </p>
                                <p className="text-sm text-slate-600">
                                  {activity.count} redemptions
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-purple-600">
                                {activity.value}
                              </p>
                              <p className="text-xs text-slate-600">coins</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-12 shadow-xl text-center">
                  <p className="text-xl font-semibold text-slate-600">
                    No redemption data available yet
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    Start redeeming coins to see analytics
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Redemption History */}
          {active === 0 && (
            <>
              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 bg-white/80 backdrop-blur-lg  rounded-2xl p-6 shadow-xl">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2 ">
                    Date Range
                  </label>
                  <select
                    value={filters.dateRange}
                    onChange={(e) =>
                      setFilters({ ...filters, dateRange: e.target.value })
                    }
                    className="w-full h-max px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 outline-none font-medium bg-white"
                  >
                    <option value="all">All Time</option>
                    <option value="today">Today</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Sort By
                  </label>
                  <select
                    value={filters.sortBy}
                    onChange={(e) =>
                      setFilters({ ...filters, sortBy: e.target.value })
                    }
                    className="w-full h-max px-4 py-3 rounded-xl border-2 border-purple-200 focus:border-purple-500 outline-none font-medium bg-white"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="highest">Highest Coins</option>
                    <option value="lowest">Lowest Coins</option>
                  </select>
                </div>
              </div>

              {/* History Table */}
              {currentData.length > 0 ? (
                <>
                  <div className="bg-white/90 backdrop-blur-lg rounded-2xl shadow-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                          <tr>
                            <th className="px-6 py-4 text-left text-sm font-bold">
                              #
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold">
                              Activity
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold">
                              Customer Phone
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold">
                              Coins
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold">
                              Date
                            </th>
                            <th className="px-6 py-4 text-left text-sm font-bold">
                              Time
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentData.map((record, idx) => (
                            <tr
                              key={idx}
                              className="border-b border-slate-200 hover:bg-purple-50 transition-colors"
                            >
                              <td className="px-6 py-4 text-sm font-medium text-slate-700">
                                {(currentPage - 1) * recordsPerPage + idx + 1}
                              </td>
                              <td className="px-6 py-4 text-sm font-semibold text-slate-800">
                                {record.activity}
                              </td>
                              <td className="px-6 py-4 text-sm font-semibold text-slate-800">
                                {record.redeemOff || "NA"}
                              </td>
                              <td className="px-6 py-4">
                                <span className="inline-block px-3 py-1 bg-gradient-to-r from-purple-500 to-indigo-500 text-white text-sm font-bold rounded-full">
                                  {record.coins} coins
                                </span>
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-600">
                                {new Date(record.timestamp).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  }
                                )}
                              </td>
                              <td className="px-6 py-4 text-sm text-slate-600">
                                {new Date(record.timestamp).toLocaleTimeString(
                                  "en-US",
                                  {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  }
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-2 mt-6">
                      <button
                        onClick={() =>
                          setCurrentPage((prev) => Math.max(1, prev - 1))
                        }
                        disabled={currentPage === 1}
                        className="px-4 py-2 rounded-xl bg-white text-purple-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-50 transition-colors shadow-md"
                      >
                        Previous
                      </button>
                      <span className="px-4 py-2 bg-white rounded-xl font-semibold text-slate-700 shadow-md">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() =>
                          setCurrentPage((prev) =>
                            Math.min(totalPages, prev + 1)
                          )
                        }
                        disabled={currentPage === totalPages}
                        className="px-4 py-2 rounded-xl bg-white text-purple-600 font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-purple-50 transition-colors shadow-md"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-white/90 backdrop-blur-lg rounded-2xl p-12 shadow-xl text-center">
                  <p className="text-xl font-semibold text-slate-600">
                    No redemption history found
                  </p>
                  <p className="text-sm text-slate-500 mt-2">
                    {filters.dateRange !== "all" || filters.sortBy !== "newest"
                      ? "Try adjusting your filters"
                      : "Start redeeming coins to see your history"}
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeProfile;
